import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["draggable"]

  connect() {
    this.debugTimeouts = new Map()
    this.draggableTargets.forEach(draggable => {
      this.debug('Enabling drag for item', {
        type: draggable.dataset.itemType,
        id: draggable.dataset.itemId
      })
      draggable.setAttribute("draggable", true)
    })
  }

  debug(message, data = {}, debounceMs = 100) {
    if (process.env.NODE_ENV !== 'development') return

    const key = `${message}-${JSON.stringify(data)}`
    if (this.debugTimeouts.has(key)) {
      clearTimeout(this.debugTimeouts.get(key))
    }

    this.debugTimeouts.set(key, setTimeout(() => {
      console.debug(`[DragDrop] ${message}`, data)
      this.debugTimeouts.delete(key)
    }, debounceMs))
  }

  // Helper to get element context for debugging
  getElementContext(element) {
    if (!element) return null

    const context = {
      tag: element.tagName?.toLowerCase(),
      id: element.id,
      classes: element.className,
      itemType: element.dataset?.itemType,
      itemId: element.dataset?.itemId,
      parentType: element.dataset?.parentType,
      parentId: element.dataset?.parentId,
      dataAttributes: {},
      parentContext: null
    }

    // Get all data attributes
    Object.keys(element.dataset || {}).forEach(key => {
      context.dataAttributes[key] = element.dataset[key]
    })

    // Get parent context (one level up)
    if (element.parentElement && element.parentElement !== this.element) {
      context.parentContext = {
        tag: element.parentElement.tagName?.toLowerCase(),
        id: element.parentElement.id,
        classes: element.parentElement.className,
        itemType: element.parentElement.dataset?.itemType,
        itemId: element.parentElement.dataset?.itemId
      }
    }

    return context
  }

  // Find the actual draggable parent from any child element
  findDraggableParent(element) {
    const draggable = element.closest('[data-drag-drop-target="draggable"]')
    if (!draggable) return null

    // Ensure we have the required data attributes
    if (!draggable.dataset.itemType || !draggable.dataset.itemId) {
      return null
    }

    return draggable
  }

  // Find the container for a specific item type within a parent
  findContainerInParent(parent, itemType) {
    this.debug("Finding container in parent", {
      parent: this.getElementContext(parent),
      itemType,
      parentHTML: parent?.outerHTML
    });

    if (!parent) {
      this.debug("No parent found");
      return null;
    }

    // For objectives, find the objectives-container
    if (itemType === 'objective') {
      const container = this.element.querySelector('.objectives-container');
      if (container) {
        this.debug("Found objectives container", {
          container: this.getElementContext(container)
        });
        return container;
      }
    }

    // For initiatives, find the initiatives-container within the parent key result's structure
    if (itemType === 'initiative') {
      // First check the next sibling which should be the div.mt-2.space-y-1.pl-8
      let container = null;
      let current = parent;

      // Look through next siblings until we find the initiatives container
      while (current && current.nextElementSibling) {
        current = current.nextElementSibling;

        // Check if this is the initiatives container
        if (current.classList.contains('initiatives-container')) {
          container = current;
          break;
        }

        // Check if this is the wrapper div that contains the initiatives container
        if (current.classList.contains('mt-2') && current.classList.contains('space-y-1')) {
          container = current.querySelector('.initiatives-container');
          if (container) break;
        }
      }

      if (container) {
        this.debug("Found initiatives container", {
          container: this.getElementContext(container),
          containerHTML: container.outerHTML
        });
        return container;
      }

      this.debug("No initiatives container found", {
        parentSiblings: Array.from(parent.parentElement?.children || [])
          .map(el => this.getElementContext(el))
      });
    }

    // For key results, find the container using team ID and objective ID
    if (itemType === 'key_result') {
      const teamId = this.element.dataset.teamId;
      if (!teamId) {
        this.debug('Missing team ID', {
          elementDataset: this.element.dataset,
          elementClasses: this.element.className
        });
        return null;
      }

      // Debug all potential containers in the entire controller element
      const allTurboFrames = this.element.querySelectorAll('turbo-frame');
      this.debug('All turbo-frames in controller', {
        count: allTurboFrames.length,
        teamId,
        frames: Array.from(allTurboFrames).map(frame => ({
          id: frame.id,
          parentClasses: frame.parentElement?.className,
          isKeyResults: frame.id?.endsWith('_keyresults'),
          framePattern: `team_${teamId}_objective_\\d+_keyresults`,
          matchesPattern: frame.id?.match(new RegExp(`team_${teamId}_objective_(\\d+)_keyresults`)),
          objectiveId: frame.id?.match(new RegExp(`team_${teamId}_objective_(\\d+)_keyresults`))?.[1]
        }))
      });

      // Strategy 1: Find turbo-frame by ID pattern with team ID
      const objectiveId = parent.dataset.itemId;
      const keyResultsFrame = Array.from(allTurboFrames).find(frame => {
        const pattern = new RegExp(`team_${teamId}_objective_${objectiveId}_keyresults`);
        return pattern.test(frame.id);
      });

      if (keyResultsFrame) {
        this.debug('Found container via ID pattern', {
          containerId: keyResultsFrame.id,
          teamId,
          objectiveId,
          pattern: `team_${teamId}_objective_${objectiveId}_keyresults`
        });
        return keyResultsFrame;
      }

      // Strategy 2: Through keyresults-container within objective
      const keyresultsContainer = this.element.querySelector(
        `[data-item-type="objective"][data-item-id="${objectiveId}"] .keyresults-container`
      );

      if (keyresultsContainer) {
        const frames = keyresultsContainer.querySelectorAll('turbo-frame');
        const container = Array.from(frames).find(frame => {
          const pattern = new RegExp(`team_${teamId}_objective_${objectiveId}_keyresults`);
          return pattern.test(frame.id);
        });

        if (container) {
          this.debug('Found container via keyresults-container', {
            containerId: container.id,
            teamId,
            objectiveId,
            pattern: `team_${teamId}_objective_${objectiveId}_keyresults`
          });
          return container;
        }
      }
    }

    this.debug("No container found in parent");
    return null;
  }

  // Helper to get all ancestor classes
  getAncestorClasses(element) {
    const classes = []
    let current = element.parentElement
    while (current && current !== this.element) {
      if (current.className) {
        classes.push({
          tag: current.tagName?.toLowerCase(),
          classes: current.className
        })
      }
      current = current.parentElement
    }
    return classes
  }

  // Find the parent element that should contain the draggable items
  findParentElement(element, itemType) {
    const elementContext = this.getElementContext(element)
    this.debug('Finding parent element', {
      itemType,
      elementContext,
      controllerElement: {
        id: this.element.id,
        classes: this.element.className
      }
    })

    switch (itemType) {
      case 'objective':
        return this.element

      case 'key_result': {
        let current = element
        let traversalPath = []

        while (current && current !== this.element) {
          const currentContext = this.getElementContext(current)
          traversalPath.push(currentContext)

          // Check for objective
          if (current.dataset?.itemType === 'objective') {
            this.debug('Found objective parent', {
              objectiveId: current.dataset.itemId,
              path: 'Direct match',
              traversalPath
            })
            return current
          }

          // Check for keyresults container
          if (current.classList.contains('keyresults-container')) {
            const objective = current.closest('[data-item-type="objective"]')
            if (objective) {
              this.debug('Found objective parent', {
                objectiveId: objective.dataset.itemId,
                path: 'Through keyresults-container',
                containerContext: this.getElementContext(current),
                objectiveContext: this.getElementContext(objective),
                traversalPath
              })
              return objective
            }
          }

          // Check for parent with data-parent-type="objective"
          if (current.dataset?.parentType === 'objective') {
            const objectiveId = current.dataset.parentId
            const objective = this.element.querySelector(`[data-item-type="objective"][data-item-id="${objectiveId}"]`)
            if (objective) {
              this.debug('Found objective parent', {
                objectiveId: objective.dataset.itemId,
                path: 'Through parent-type attribute',
                currentContext: this.getElementContext(current),
                objectiveContext: this.getElementContext(objective),
                traversalPath
              })
              return objective
            }
          }

          current = current.parentElement
        }

        this.debug('No objective parent found', {
          elementContext,
          traversalPath,
          controllerElement: {
            id: this.element.id,
            classes: this.element.className,
            html: this.element.outerHTML
          }
        })
        return null
      }

      case 'initiative': {
        // Similar approach for initiatives
        let current = element
        while (current && current !== this.element) {
          // Direct key_result match
          if (current.dataset?.itemType === 'key_result') {
            this.debug('Found key_result parent', {
              keyResultId: current.dataset.itemId,
              path: 'Direct match'
            })
            return current
          }

          // Through initiatives container
          if (current.classList.contains('initiatives-container')) {
            const keyResult = current.closest('[data-item-type="key_result"]')
            if (keyResult) {
              this.debug('Found key_result parent', {
                keyResultId: keyResult.dataset.itemId,
                path: 'Through initiatives-container'
              })
              return keyResult
            }
          }

          // Through parent-type attribute
          if (current.dataset?.parentType === 'keyresult') {
            const keyResultId = current.dataset.parentId
            const keyResult = this.element.querySelector(`[data-item-type="key_result"][data-item-id="${keyResultId}"]`)
            if (keyResult) {
              this.debug('Found key_result parent', {
                keyResultId: keyResult.dataset.itemId,
                path: 'Through parent-type attribute'
              })
              return keyResult
            }
          }

          current = current.parentElement
        }

        this.debug('No key_result parent found', {
          elementId: element.dataset?.itemId,
          parentType: element.dataset?.parentType,
          parentId: element.dataset?.parentId,
          elementHtml: element.outerHTML
        })
        return null
      }

      default:
        return null
    }
  }

  findContainer(itemType, element) {
    const elementContext = this.getElementContext(element)
    this.debug('Finding container', {
      itemType,
      elementContext,
      controllerElement: {
        id: this.element.id,
        classes: this.element.className
      }
    }, 500)

    const draggable = this.findDraggableParent(element)
    if (!draggable) {
      this.debug('No draggable parent found', {
        elementContext,
        draggableSearch: {
          closestDraggable: element.closest('[data-drag-drop-target]')?.outerHTML,
          parentElements: Array.from(element.parentElements || []).map(p => this.getElementContext(p))
        }
      })
      return null
    }

    const parent = this.findParentElement(draggable, itemType)
    if (!parent) {
      this.debug('No parent element found', {
        itemType,
        draggableContext: this.getElementContext(draggable),
        elementContext
      })
      return null
    }

    const container = this.findContainerInParent(parent, itemType)
    this.debug('Container search result', {
      itemType,
      parentContext: this.getElementContext(parent),
      containerContext: this.getElementContext(container),
      draggableContext: this.getElementContext(draggable)
    }, 500)

    return container
  }

  dragStart(event) {
    const draggable = this.findDraggableParent(event.target)
    if (!draggable) return

    this.debug('Drag started', {
      type: draggable.dataset.itemType,
      id: draggable.dataset.itemId
    })

    draggable.classList.add("dragging")
    event.dataTransfer.effectAllowed = "move"
    event.dataTransfer.setData("application/json", JSON.stringify({
      itemType: draggable.dataset.itemType,
      itemId: draggable.dataset.itemId
    }))
  }

  dragEnd(event) {
    const draggable = this.findDraggableParent(event.target)
    if (!draggable) return

    draggable.classList.remove("dragging")

    // Clean up any lingering drag-over highlights (keep drop-placeholders — they
    // persist until a KR is moved back in or the page reloads)
    this.element.querySelectorAll('.drag-over').forEach(el => el.classList.remove('drag-over'))
  }

  dragOver(event) {
    event.preventDefault()

    const draggable = this.findDraggableParent(event.target)
    if (!draggable) return

    const draggedItem = this.element.querySelector(".dragging")
    if (!draggedItem || draggedItem === draggable) return

    const itemType = draggedItem.dataset.itemType
    if (draggable.dataset.itemType !== itemType) return

    const draggedContainer = this.findContainer(itemType, draggedItem)
    const targetContainer = this.findContainer(itemType, draggable)

    if (!draggedContainer || !targetContainer) {
      this.debug('Container not found', {
        draggedContainerId: draggedContainer?.id,
        targetContainerId: targetContainer?.id
      })
      return
    }

    // For key results, allow moving between different containers
    if (itemType === 'key_result') {
      // Both containers should be turbo frames for key results
      if (draggedContainer.tagName.toLowerCase() !== 'turbo-frame' ||
        targetContainer.tagName.toLowerCase() !== 'turbo-frame' ||
        !draggedContainer.id.endsWith('_keyresults') ||
        !targetContainer.id.endsWith('_keyresults')) {
        this.debug('Invalid containers for key result movement', {
          draggedContainer: this.getElementContext(draggedContainer),
          targetContainer: this.getElementContext(targetContainer)
        })
        return
      }

      // Cross-container moves are handled entirely by dragOverContainer — bail out
      // to avoid both handlers fighting over the same DOM move every ~16ms
      if (draggedContainer !== targetContainer) return
    } else if (draggedContainer !== targetContainer) {
      // For other types, containers must match
      this.debug('Container mismatch', {
        draggedContainerId: draggedContainer?.id,
        targetContainerId: targetContainer?.id
      })
      return
    }

    const items = [...targetContainer.querySelectorAll(`[data-item-type="${itemType}"]`)]
    const draggedIndex = items.indexOf(draggedItem)
    const dropIndex = items.indexOf(draggable)

    this.debug('Drag over positions', {
      itemType,
      draggedId: draggedItem.dataset.itemId,
      targetId: draggable.dataset.itemId,
      draggedIndex,
      dropIndex,
      draggedHTML: draggedItem.outerHTML,
      targetHTML: draggable.outerHTML,
      containerChildren: Array.from(targetContainer.children).map(child => ({
        type: child.dataset?.itemType,
        id: child.dataset?.itemId,
        classes: child.className
      }))
    })

    if (draggedIndex !== dropIndex || draggedContainer !== targetContainer) {
      // Find the associated content containers
      const draggedContent = this.findAssociatedContent(draggedItem, itemType)
      const targetContent = this.findAssociatedContent(draggable, itemType)

      this.debug('Associated content', {
        draggedContent: draggedContent?.map(el => this.getElementContext(el)),
        targetContent: targetContent?.map(el => this.getElementContext(el))
      })

      if (draggedIndex < dropIndex || draggedContainer !== targetContainer) {
        // Moving down or to a different container
        if (targetContent?.length) {
          const lastTargetContent = targetContent[targetContent.length - 1]
          lastTargetContent.after(draggedItem)
          draggedContent?.forEach(content => draggedItem.after(content))
        } else {
          draggable.after(draggedItem)
          draggedContent?.forEach(content => draggedItem.after(content))
        }
      } else {
        // Moving up in the same container
        draggable.before(draggedItem)
        draggedContent?.forEach(content => draggedItem.after(content))
      }

      // Update the parent references when moving between containers
      if (draggedContainer !== targetContainer) {
        const newObjectiveId = targetContainer.id.match(/team_\d+_objective_(\d+)_keyresults/)?.[1]
        if (newObjectiveId) {
          draggedItem.dataset.parentId = newObjectiveId
          draggedItem.dataset.parentType = 'objective'
        }

      }
    }
  }

  // Handle dragover on a keyresults-container (supports dropping into empty objectives)
  dragOverContainer(event) {
    event.preventDefault()

    const draggedItem = this.element.querySelector(".dragging")
    if (!draggedItem) return

    // Only handle key_result items
    if (draggedItem.dataset.itemType !== 'key_result') return

    // Find the target keyresults turbo frame within this container
    const containerDiv = event.target.closest('.keyresults-container')
    if (!containerDiv) return

    // Add visual feedback
    containerDiv.classList.add('drag-over')

    const targetFrame = containerDiv.querySelector('turbo-frame[id$="_keyresults"]')
    if (!targetFrame) return

    // Find where the dragged item currently lives
    const draggedContainer = draggedItem.closest('turbo-frame[id$="_keyresults"]')
    if (draggedContainer === targetFrame) return // already in this container — no DOM mutation needed

    // Guard: if the item is somehow already a child of the target (race / rapid events), bail
    if (targetFrame.contains(draggedItem)) return

    // Remember the source container before moving
    const sourceContainerDiv = draggedContainer?.closest('.keyresults-container')

    // Move the dragged item (and its associated content) into the target frame
    const draggedContent = this.findAssociatedContent(draggedItem, 'key_result')
    targetFrame.appendChild(draggedItem)
    draggedContent?.forEach(content => targetFrame.appendChild(content))

    // Remove any drop placeholder from the target (it now has a KR)
    this.removeDropPlaceholder(containerDiv)

    // If the source container's turbo-frame is now empty, inject a drop placeholder
    // so it remains a visible drag target (the server-rendered placeholder only exists on page load)
    if (draggedContainer && !draggedContainer.querySelector('[data-item-type="key_result"]')) {
      this.ensureDropPlaceholder(sourceContainerDiv)
    }

    // Update parent references
    const newObjectiveId = targetFrame.id.match(/team_\d+_objective_(\d+)_keyresults/)?.[1]
    if (newObjectiveId) {
      draggedItem.dataset.parentId = newObjectiveId
      draggedItem.dataset.parentType = 'objective'
    }
  }

  // Handle dragleave on a keyresults-container (remove visual feedback)
  dragLeaveContainer(event) {
    const containerDiv = event.target.closest('.keyresults-container')
    if (!containerDiv) return

    // Only remove if we're actually leaving the container (not entering a child)
    if (!containerDiv.contains(event.relatedTarget)) {
      containerDiv.classList.remove('drag-over')
    }
  }

  // Handle drop on a keyresults-container (supports dropping into empty objectives)
  async dropOnContainer(event) {
    event.preventDefault()

    // Remove visual feedback
    const containerDiv = event.target.closest('.keyresults-container')
    if (containerDiv) containerDiv.classList.remove('drag-over')

    try {
      const data = JSON.parse(event.dataTransfer.getData("application/json"))
      const teamId = this.element.dataset.teamId
      const periodId = this.element.dataset.periodId

      if (!teamId || !periodId) {
        throw new Error("Missing team or period context")
      }

      // Find the target keyresults turbo frame
      const containerDiv = event.target.closest('.keyresults-container')
      if (!containerDiv) {
        throw new Error("Container div not found for drop")
      }

      const container = containerDiv.querySelector('turbo-frame[id$="_keyresults"]')
      if (!container) {
        throw new Error("Keyresults turbo frame not found for drop")
      }

      const items = Array.from(
        container.querySelectorAll(`[data-item-type="${data.itemType}"]`)
      ).map(item => item.dataset.itemId)

      const objectiveMatch = container.id?.match(/objective_(\d+)/)
      const parentInfo = objectiveMatch
        ? { type: 'objective', id: objectiveMatch[1] }
        : null

      this.debug('Sending container drop reorder request', {
        type: data.itemType,
        items,
        containerId: container.id,
        parentInfo
      })

      const endpointType = data.itemType === 'key_result' ? 'keyresults' : `${data.itemType}s`
      await this.sendReorderRequest(teamId, periodId, endpointType, items, parentInfo)

    } catch (error) {
      console.error("[DragDrop] Container drop error:", error)
      this.element.dispatchEvent(
        new CustomEvent("drag-drop:error", {
          detail: { error: error.message },
          bubbles: true
        })
      )
    }
  }

  // Inject a drop placeholder into an empty keyresults-container so it
  // remains a visible drag target after all KRs have been moved out client-side.
  // Prefers restoring the server-rendered placeholder when available.
  ensureDropPlaceholder(containerDiv) {
    if (!containerDiv) return
    if (containerDiv.querySelector('.drop-placeholder')) return // already has one

    // Prefer the server-rendered placeholder (the "Make X Measurable" block) if it exists
    const serverPlaceholder = containerDiv.querySelector('turbo-frame[id$="_keyresults_placeholder"]')
    if (serverPlaceholder && serverPlaceholder.children.length > 0) {
      serverPlaceholder.style.display = ''
      return
    }

    // Fall back to a skeleton KR row that matches the okr-grid 8-column layout
    const placeholder = document.createElement('div')
    placeholder.className = 'drop-placeholder okr-grid okr-row okr-nested'

    // Col 1 (2rem) – drag handle
    const handleCol = document.createElement('div')
    handleCol.className = 'drag-handle-container relative group'
    const handle = document.createElement('div')
    handle.className = 'drag-handle'
    handle.textContent = '\u2630'
    handleCol.appendChild(handle)
    placeholder.appendChild(handleCol)

    // Col 2 (2.5rem) – skeleton icon circle
    const iconCol = document.createElement('div')
    iconCol.className = 'okr-icon-container'
    const iconCircle = document.createElement('div')
    iconCircle.className = 'w-5 h-5 rounded-full bg-base-content/10'
    iconCol.appendChild(iconCircle)
    placeholder.appendChild(iconCol)

    // Col 3 (1fr) – skeleton text bar
    const textCol = document.createElement('div')
    textCol.className = 'min-w-0'
    const textBar = document.createElement('div')
    textBar.className = 'h-4 rounded bg-base-content/10 w-2/3'
    textCol.appendChild(textBar)
    placeholder.appendChild(textCol)

    // Col 4 (auto) – skeleton avatar circle
    const ownerCol = document.createElement('div')
    ownerCol.className = 'flex items-center justify-center'
    const avatarCircle = document.createElement('div')
    avatarCircle.className = 'w-8 h-8 rounded-full bg-base-content/10'
    ownerCol.appendChild(avatarCircle)
    placeholder.appendChild(ownerCol)

    // Col 5 (8rem) – skeleton progress bar + percentage/status
    const progressCol = document.createElement('div')
    progressCol.className = 'px-4'
    const progressBar = document.createElement('div')
    progressBar.className = 'h-2.5 rounded-full bg-base-content/10 w-full'
    progressCol.appendChild(progressBar)
    const progressMeta = document.createElement('div')
    progressMeta.className = 'flex items-center gap-1 mt-1'
    const pctSkeleton = document.createElement('div')
    pctSkeleton.className = 'h-3 rounded bg-base-content/8 w-8'
    const statusSkeleton = document.createElement('div')
    statusSkeleton.className = 'h-3 rounded bg-base-content/8 w-14'
    progressMeta.appendChild(pctSkeleton)
    progressMeta.appendChild(statusSkeleton)
    progressCol.appendChild(progressMeta)
    placeholder.appendChild(progressCol)

    // Col 6 (8rem) – skeleton check-in text
    const checkinCol = document.createElement('div')
    checkinCol.className = 'text-sm text-base-content/60 truncate'
    const checkinBar = document.createElement('div')
    checkinBar.className = 'h-3 rounded bg-base-content/8 w-12'
    checkinCol.appendChild(checkinBar)
    placeholder.appendChild(checkinCol)

    // Col 7 (8rem) – skeleton updated-at text
    const updatedCol = document.createElement('div')
    updatedCol.className = 'text-sm text-base-content/60 truncate'
    const updatedBar = document.createElement('div')
    updatedBar.className = 'h-3 rounded bg-base-content/8 w-16'
    updatedCol.appendChild(updatedBar)
    placeholder.appendChild(updatedCol)

    // Col 8 (7rem) – empty (confidence is objectives-only)
    placeholder.appendChild(document.createElement('div'))

    containerDiv.appendChild(placeholder)
  }

  // Remove/hide drop placeholders when a KR is dropped into the container.
  // Removes client-side placeholder and hides server-rendered placeholder.
  removeDropPlaceholder(containerDiv) {
    if (!containerDiv) return
    const clientPlaceholder = containerDiv.querySelector('.drop-placeholder')
    if (clientPlaceholder) clientPlaceholder.remove()

    const serverPlaceholder = containerDiv.querySelector('turbo-frame[id$="_keyresults_placeholder"]')
    if (serverPlaceholder) serverPlaceholder.style.display = 'none'
  }

  // Helper to find associated content (keyresults container, initiatives container, etc.)
  findAssociatedContent(element, itemType) {
    if (!element) return null

    const content = []
    let current = element.nextElementSibling

    if (itemType === 'objective') {
      // For objectives, look for keyresults container and new keyresult frame
      while (current && (
        current.classList.contains('mt-2') ||
        current.classList.contains('keyresults-container') ||
        current.tagName.toLowerCase() === 'turbo-frame'
      )) {
        content.push(current)
        current = current.nextElementSibling
      }
    } else if (itemType === 'key_result') {
      // For key results, look for initiatives container
      while (current && (
        current.classList.contains('mt-2') ||
        current.classList.contains('initiatives-container')
      )) {
        content.push(current)
        current = current.nextElementSibling
      }
    }

    return content
  }

  async drop(event) {
    event.preventDefault()

    try {
      const data = JSON.parse(event.dataTransfer.getData("application/json"))
      const teamId = this.element.dataset.teamId
      const periodId = this.element.dataset.periodId

      if (!teamId || !periodId) {
        throw new Error("Missing team or period context")
      }

      const container = this.findContainer(data.itemType, event.target)
      if (!container) {
        throw new Error(`Container not found for ${data.itemType}`)
      }

      const items = Array.from(
        container.querySelectorAll(`[data-item-type="${data.itemType}"]`)
      ).map(item => item.dataset.itemId)

      // Extract parent info directly from the container's ID pattern
      // (e.g., "team_1_objective_42_keyresults" or "team_1_keyresult_7_initiatives")
      // This is reliable even after dragOver has mutated the DOM
      const objectiveMatch = container.id?.match(/objective_(\d+)/)
      const keyresultMatch = container.id?.match(/keyresult_(\d+)/)
      const parentInfo = objectiveMatch
        ? { type: 'objective', id: objectiveMatch[1] }
        : keyresultMatch
        ? { type: 'keyresult', id: keyresultMatch[1] }
        : null

      this.debug('Sending reorder request', {
        type: data.itemType,
        items,
        containerId: container.id,
        parentInfo
      })

      // Handle API endpoint naming
      const endpointType = data.itemType === 'key_result' ? 'keyresults' : `${data.itemType}s`
      await this.sendReorderRequest(teamId, periodId, endpointType, items, parentInfo)

    } catch (error) {
      console.error("[DragDrop] Error:", error)
      this.element.dispatchEvent(
        new CustomEvent("drag-drop:error", {
          detail: { error: error.message },
          bubbles: true
        })
      )
    }
  }

  async sendReorderRequest(teamId, periodId, itemType, items, parentInfo = null) {
    // Build the base request body
    const body = {
      item_ids: items
    }

    // For both key results and initiatives, we need to find the parent
    if (itemType === 'keyresults' || itemType === 'initiatives') {
      // Use parentInfo from container ID if available (reliable after cross-parent drag)
      if (parentInfo) {
        if (parentInfo.type === 'objective') {
          body.objective_id = parentInfo.id
        } else if (parentInfo.type === 'keyresult') {
          body.keyresult_id = parentInfo.id
        }

        this.debug('Using container-derived parent for reorder', {
          itemType,
          parentInfo,
          items
        })
      } else {
        // Fallback: DOM traversal from first item (works for same-container reorders)
        const firstItem = this.element.querySelector(`[data-item-id="${items[0]}"]`)
        const parentType = itemType === 'keyresults' ? 'key_result' : 'initiative'
        const parent = this.findParentElement(firstItem, parentType)

        if (!parent) {
          throw new Error(`Parent not found for ${parentType}`)
        }

        const parentItemType = parent.dataset.itemType
        const parentId = parent.dataset.itemId

        if (parentItemType === 'objective') {
          body.objective_id = parentId
        } else if (parentItemType === 'key_result') {
          body.keyresult_id = parentId
        }

        this.debug('Using DOM-traversal parent for reorder', {
          itemType,
          parentItemType,
          parentId,
          items
        })
      }
    } else {
      this.debug('Sending objective reorder request', {
        teamId,
        periodId,
        items,
        body
      })
    }

    // Initiatives are handled as key results in the backend
    const endpointType = itemType === 'initiatives' ? 'keyresults' : itemType

    const response = await fetch(`/teams/${teamId}/periods/${periodId}/${endpointType}/reorder`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("[name='csrf-token']").content
      },
      body: JSON.stringify(body)
    })

    if (!response.ok) {
      throw new Error(`Server error: ${response.status}`)
    }

    return response
  }
} 